// <s-values-slider> auto-advances a scroll-snap track one slide at a time while it is scrollable (mobile).
// Any touch, wheel, hover or focus stops autoplay for good; reduced motion never autoplays. Dots jump to a slide.
class ValuesSlider extends HTMLElement {
  #track = null;
  #slides = [];
  #dots = [];
  #timer = 0;
  #stopped = false;
  #controller = null;

  connectedCallback() {
    this.#track = this.querySelector('[data-slider-track]');
    if (!this.#track) return;
    this.#slides = Array.from(this.#track.children);
    this.#dots = Array.from(this.querySelectorAll('[data-slider-dot]'));
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#stopped = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const stop = () => { this.#stopped = true; window.clearInterval(this.#timer); };
    ['pointerdown', 'wheel', 'focusin', 'pointerenter'].forEach((type) => {
      this.addEventListener(type, stop, { signal, passive: true });
    });
    this.#track.addEventListener('scroll', () => this.#syncDots(), { signal, passive: true });
    this.#dots.forEach((dot, index) => {
      dot.addEventListener('click', () => { stop(); this.#goTo(index); }, { signal });
    });

    if (!this.#stopped) {
      const interval = Number(this.dataset.interval) || 4000;
      this.#timer = window.setInterval(() => this.#advance(), interval);
    }
    this.#syncDots();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    window.clearInterval(this.#timer);
  }

  #isScrollable() {
    return this.#track.scrollWidth > this.#track.clientWidth + 1;
  }

  #currentIndex() {
    const width = this.#track.clientWidth || 1;
    return Math.round(Math.abs(this.#track.scrollLeft) / width);
  }

  #advance() {
    if (this.#stopped || document.hidden || !this.#isScrollable()) return;
    this.#goTo((this.#currentIndex() + 1) % this.#slides.length);
  }

  #goTo(index) {
    if (!this.#slides[index]) return;
    // Slides are full-width, so the offset is index × track width; RTL tracks scroll toward negative scrollLeft.
    const direction = getComputedStyle(this.#track).direction === 'rtl' ? -1 : 1;
    this.#track.scrollTo({ left: direction * index * this.#track.clientWidth, behavior: 'smooth' });
  }

  #syncDots() {
    const current = this.#currentIndex();
    this.#dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === current)));
  }
}

if (!customElements.get('s-values-slider')) customElements.define('s-values-slider', ValuesSlider);
